import { JSX } from 'react';
import Container from '@ui/container/container';
import OptionalComponent from '@ui/optional-component/optional-component';
import Text from '@ui/text/text/text';
// commenting out tooltip for now as it no longer appears in Figma
// import { ICONS } from '@ui/icons/types';
// import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
// import Icon from '@ui/icons/icon';
import styles from './stats-card.module.css';

type TStatsCardProps = {
  title: string;
  tooltip: string;
  Icon: JSX.Element;
  value: number | string; // may be string for cases like video "1h 30m"
  subvalue?: string | null;
  loading?: boolean;
};

// array of zero-equivalent values that will be ignored in the ui when used as a subvalue
const valuesToIgnore: string[] = ['0', '0h 0m', '0%'];

// commenting out tooltip for now as it no longer appears in Figma
const StatsCard = ({ title, /*tooltip,*/ Icon: LabelIcon, value, loading, subvalue }: TStatsCardProps) => {
  // zero-equivalent values (including the negative "no data" sentinel) render as 0 in a muted color
  const isZeroValue =
    (value != null && valuesToIgnore.includes(value.toLocaleString())) || (typeof value === 'number' && value < 0);

  const valueDisplay = () => {
    if (loading) {
      return <>&nbsp;</>;
    }

    if (typeof value === 'string') {
      return <>{value}</>;
    }

    if (typeof value === 'number') {
      // treat negative sentinel values (e.g. -1 for "no data") as 0
      return <>{value < 0 ? '0' : value.toLocaleString()}</>;
    }

    return <>0</>;
  };

  return (
    <Container
      className={subvalue ? 'flex-2' : 'flex-1'}
      background="rgba(255, 255, 255, 0.05)"
      gap="6px"
      padding="12px"
      borderRadius="12px"
      height="64px"
      width="124px"
      minWidth="124px"
      maxWidth="124px"
      data-component="StatsCard"
    >
      <Container className="flex fd-row jc-flex-start ai-center gap-6" margin="0 0 4px 0">
        <Text className="C1" design="GlobalIcoText3">
          {title}
        </Text>
        {/* 
        // commenting out tooltip for now as it no longer appears in Figma
        // leaving in case we need to re-enable it later

        <TooltipPortal
          animationType="slide-up"
          keepOpenOnContentHover={false}
          childrenWrapperClass="flex fd-row jc-center ai-center"
          disabled={!tooltip.length}
          tooltipContent={
            <Text maxWidth="250px" className="C1">
              {tooltip}
            </Text>
          }
        >
          <Icon name={ICONS.INFO_CIRCLE} size={10} color="var(--gray-300)" />
        </TooltipPortal> 
        */}
      </Container>

      <Container className="flex fd-row jc-flex-start ai-center">
        <Container height="20px" width="16px" margin="0 6px 0 0" className="flex fd-row jc-center ai-center">
          {LabelIcon}
        </Container>
        <Text
          skeletonLoader={loading}
          skeletonLoaderBackground={{ start: 'transparent', end: 'var(--gray-800)' }}
          tag="h5"
          className="H5"
          width={'100%'}
          color={isZeroValue ? 'var(--gray-400)' : undefined}
          height="20px"
          clamp={1}
        >
          {valueDisplay()}
          <OptionalComponent
            display={value !== undefined && !!subvalue && !valuesToIgnore.includes(subvalue.toLocaleString())}
          >
            <Text design="GlobalIcoText3" className={styles.subvalue}>
              {' ' + subvalue}
            </Text>
          </OptionalComponent>
        </Text>
      </Container>
    </Container>
  );
};

export default StatsCard;
